import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  useEffect,
  useMemo,
  useState,
  type DragEvent,
  type JSX,
  type ReactNode,
} from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import clsx from 'clsx';
import {
  BookOpen,
  Check,
  Download,
  EyeOff,
  FileText,
  GripVertical,
  History,
  Layers,
  Pencil,
  Plus,
  Send,
  Trash2,
  Upload,
  Users,
  X,
} from 'lucide-react';
import { apiRequest, ApiError, apiListAll } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { useConfirm } from '../components/ui/Confirm';
import { formatDate } from '../lib/format';
import { Button } from '../components/ui/Button';
import { Drawer } from '../components/ui/Drawer';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { TextField, TextAreaField } from '../components/ui/Field';
import { GatedSection } from '../components/shared/GatedSection';
import { DetailHeader } from '../components/shared/DetailHeader';
import { KpiStrip } from '../components/shared/KpiStrip';
import { RelationPanel } from '../components/shared/RelationPanel';
import { ActivityPanel } from '../components/shared/ActivityPanel';
import {
  CourseDrawer,
  type CourseDrawerMode,
} from '../components/academics/CourseDrawer';
import {
  humanBytes,
  programLabel,
  type BatchRecord,
  type CourseWithLessons,
  type LessonSummary,
  type ResourceRecord,
} from '../components/academics/types';

export const Route = createFileRoute('/_authed/academics/courses/$courseId')({
  component: CourseDetailPage,
});

const API_ORIGIN =
  (import.meta.env.VITE_API_ORIGIN as string | undefined) ??
  'https://api.babulilmonline.com';

// The backend may roll out aggregate counters incrementally; treat them as
// optional and fall back to `course.lessons.length` for the lesson tile.
interface CourseDetail extends CourseWithLessons {
  activeBatchCount?: number;
  enrolledStudentCount?: number;
  resourceCount?: number;
  lessonCount?: number;
}

interface BatchWithEnrolled extends BatchRecord {
  enrolledCount?: number;
}

type Tab = 'overview' | 'lessons' | 'batches' | 'resources' | 'history';

const TABS: ReadonlyArray<{ value: Tab; label: string; icon: typeof BookOpen }> = [
  { value: 'overview', label: 'Overview', icon: FileText },
  { value: 'lessons', label: 'Lessons', icon: BookOpen },
  { value: 'batches', label: 'Batches', icon: Users },
  { value: 'resources', label: 'Resources', icon: Layers },
  { value: 'history', label: 'History', icon: History },
];

function CourseDetailPage(): JSX.Element {
  const { courseId } = Route.useParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const canManage = role === 'super_admin' || role === 'academic_admin';

  const [tab, setTab] = useState<Tab>('overview');
  const [editDrawer, setEditDrawer] = useState<CourseDrawerMode | null>(null);

  const courseQuery = useQuery({
    queryKey: ['course', 'detail', courseId] as const,
    queryFn: ({ signal }) =>
      apiRequest<CourseDetail>(`/api/courses/${encodeURIComponent(courseId)}`, { signal }),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const course = courseQuery.data;

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['course', 'detail', courseId] });
    void queryClient.invalidateQueries({ queryKey: ['courses'] });
  };

  const publishMutation = useMutation({
    mutationFn: (nextIsActive: boolean) =>
      apiRequest<{ ok: true }>(`/api/courses/${encodeURIComponent(courseId)}`, {
        method: 'PATCH',
        body: { isActive: nextIsActive },
      }),
    onSuccess: (_res, nextIsActive) => {
      toast(nextIsActive ? 'Course published.' : 'Course unpublished.', {
        variant: 'success',
      });
      invalidate();
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not update course.', {
        variant: 'error',
      });
    },
  });

  const togglePublish = async (): Promise<void> => {
    if (!course) return;
    const next = !course.isActive;
    const ok = await confirm({
      title: next ? 'Publish this course?' : 'Unpublish this course?',
      description: next
        ? 'Published courses can be assigned to new batches.'
        : 'New batches will not be able to reference this course while it is unpublished.',
      confirmLabel: next ? 'Publish' : 'Unpublish',
      variant: next ? 'default' : 'danger',
    });
    if (!ok) return;
    publishMutation.mutate(next);
  };

  const kpiItems = useMemo(() => {
    if (!course) return [];
    const lessonCount = course.lessonCount ?? course.lessons.length;
    return [
      {
        label: 'Lessons',
        value: lessonCount,
        onClick: () => setTab('lessons'),
      },
      {
        label: 'Active batches',
        value: course.activeBatchCount ?? '—',
        onClick: () => setTab('batches'),
      },
      {
        label: 'Enrolled',
        value: course.enrolledStudentCount ?? '—',
      },
      {
        label: 'Duration',
        value:
          course.durationWeeksMin === course.durationWeeksMax
            ? `${course.durationWeeksMin} weeks`
            : `${course.durationWeeksMin}–${course.durationWeeksMax} weeks`,
      },
      {
        label: 'Cadence',
        value: `${course.sessionsPerWeek}/wk · ${course.sessionMinutes}m`,
      },
      {
        label: 'Resources',
        value: course.resourceCount ?? '—',
        onClick: () => setTab('resources'),
      },
    ];
  }, [course]);

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto w-full">
      <GatedSection
        error={courseQuery.error}
        title="Course"
        onRetry={() => courseQuery.refetch()}
      >
        {courseQuery.isLoading || !course ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <DetailHeader
              title={course.name}
              sublabel={
                <>
                  <code className="rounded bg-[color:var(--color-surface)] px-1.5 py-0.5 font-mono text-[11px] text-[color:var(--color-text-muted)]">
                    {course.slug}
                  </code>
                  <ProgramChip program={course.program} />
                </>
              }
              statusChip={<ActiveChip active={course.isActive} />}
              backTo="/academics/courses"
              actions={
                canManage ? (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<Pencil size={14} aria-hidden="true" />}
                      onClick={() => setEditDrawer({ kind: 'edit', id: course.id })}
                    >
                      Edit course
                    </Button>
                    <Button
                      variant={course.isActive ? 'ghost' : 'primary'}
                      size="sm"
                      icon={
                        course.isActive ? (
                          <EyeOff size={14} aria-hidden="true" />
                        ) : (
                          <Send size={14} aria-hidden="true" />
                        )
                      }
                      onClick={() => {
                        void togglePublish();
                      }}
                      loading={publishMutation.isPending}
                    >
                      {course.isActive ? 'Unpublish' : 'Publish'}
                    </Button>
                  </>
                ) : null
              }
            />

            <KpiStrip items={kpiItems} />

            <div
              role="tablist"
              aria-label="Course sections"
              className="flex flex-wrap gap-1 border-b border-[color:var(--color-border)]"
            >
              {TABS.map(({ value, label, icon: Icon }) => {
                const active = tab === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTab(value)}
                    className={clsx(
                      'inline-flex items-center gap-1.5 rounded-t-md px-3 py-2 text-sm font-medium transition-colors',
                      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
                      active
                        ? 'border-b-2 border-gold-500 text-[color:var(--color-text)]'
                        : 'text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]',
                    )}
                  >
                    <Icon size={14} aria-hidden="true" />
                    {label}
                  </button>
                );
              })}
            </div>

            {tab === 'overview' ? <OverviewTab course={course} /> : null}
            {tab === 'lessons' ? (
              <LessonsTab
                course={course}
                canManage={canManage}
                onChanged={invalidate}
              />
            ) : null}
            {tab === 'batches' ? <BatchesTab courseId={course.id} /> : null}
            {tab === 'resources' ? <ResourcesTab courseId={course.id} /> : null}
            {tab === 'history' ? (
              <ActivityPanel
                targetType="course"
                targetId={course.id}
                title="History"
              />
            ) : null}
          </div>
        )}
      </GatedSection>

      {canManage ? (
        <CourseDrawer
          mode={editDrawer}
          onClose={() => setEditDrawer(null)}
          initial={course}
          isLoadingInitial={false}
          loadError={null}
          onSaved={() => {
            setEditDrawer(null);
            invalidate();
            toast('Course saved.', { variant: 'success' });
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline chips (kept local — the spec doesn't ship them as shared components
// yet and they aren't reused elsewhere). Mirrors the StatusBadge styling in
// the academics directory.
// ---------------------------------------------------------------------------

function ProgramChip({ program }: { program: string }): JSX.Element {
  return (
    <span className="inline-flex items-center rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-[11px] font-medium text-[color:var(--color-text-muted)]">
      {programLabel(program)}
    </span>
  );
}

function ActiveChip({ active }: { active: boolean }): JSX.Element {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold',
        active
          ? 'bg-[color:var(--color-success)]/15 text-[color:var(--color-success)]'
          : 'bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)]',
      )}
    >
      {active ? (
        <Check size={11} aria-hidden="true" />
      ) : (
        <X size={11} aria-hidden="true" />
      )}
      {active ? 'Published' : 'Unpublished'}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Overview tab
// ---------------------------------------------------------------------------

function OverviewTab({ course }: { course: CourseDetail }): JSX.Element {
  return (
    <section className="grid gap-4 lg:grid-cols-2">
      <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
          Description
        </h3>
        {course.description ? (
          <p className="mt-2 whitespace-pre-wrap text-sm text-[color:var(--color-text)]">
            {course.description}
          </p>
        ) : (
          <p className="mt-2 text-sm text-[color:var(--color-text-muted)]">
            No description provided.
          </p>
        )}
      </div>
      <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
          Details
        </h3>
        <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
          <DetailRow label="Program" value={programLabel(course.program)} />
          <DetailRow label="Slug" value={<code className="font-mono text-xs">{course.slug}</code>} />
          <DetailRow
            label="Duration"
            value={
              course.durationWeeksMin === course.durationWeeksMax
                ? `${course.durationWeeksMin} weeks`
                : `${course.durationWeeksMin}–${course.durationWeeksMax} weeks`
            }
          />
          <DetailRow
            label="Cadence"
            value={`${course.sessionsPerWeek}/wk · ${course.sessionMinutes}m`}
          />
          <DetailRow label="Status" value={course.isActive ? 'Published' : 'Unpublished'} />
          <DetailRow label="Created" value={formatDate(course.createdAt)} />
          <DetailRow label="Updated" value={formatDate(course.updatedAt)} />
        </dl>
      </div>
    </section>
  );
}

function DetailRow({ label, value }: { label: string; value: ReactNode }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-[color:var(--color-border)] pb-2 last:border-b-0 last:pb-0">
      <dt className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {label}
      </dt>
      <dd className="text-sm text-[color:var(--color-text)]">{value}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lessons tab — drag reorder + inline edit + delete + add via drawer
// ---------------------------------------------------------------------------

const lessonFormSchema = z
  .object({
    title: z.string().trim().min(2, 'Title is required').max(160),
    description: z.string().max(4000).optional(),
    expectedDurationMinutes: z
      .number()
      .int()
      .min(1)
      .max(600)
      .optional(),
  })
  .strict();

type LessonFormValues = z.infer<typeof lessonFormSchema>;

interface LessonsTabProps {
  course: CourseDetail;
  canManage: boolean;
  onChanged: () => void;
}

function LessonsTab({ course, canManage, onChanged }: LessonsTabProps): JSX.Element {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const [draftOrder, setDraftOrder] = useState<LessonSummary[] | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  // The source-of-truth is the parent's course.lessons; draftOrder mirrors it
  // optimistically during a reorder. Keep them in sync when the parent fetch
  // returns a fresh ordering.
  useEffect(() => {
    setDraftOrder(null);
  }, [course.lessons]);

  const lessons = draftOrder ?? course.lessons;

  const reorderMutation = useMutation({
    mutationFn: (lessonIds: string[]) =>
      apiRequest<{ ok: true }>(
        `/api/courses/${encodeURIComponent(course.id)}/lessons/reorder`,
        { method: 'POST', body: { lessonIds } },
      ),
    onSuccess: () => {
      toast('Lesson order saved.', { variant: 'success' });
      onChanged();
    },
    onError: (err: unknown) => {
      // Roll back the optimistic order if the server rejects.
      setDraftOrder(null);
      toast(err instanceof ApiError ? err.message : 'Could not reorder lessons.', {
        variant: 'error',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (lessonId: string) =>
      apiRequest<{ ok: true }>(`/api/lessons/${encodeURIComponent(lessonId)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      toast('Lesson removed.', { variant: 'success' });
      void queryClient.invalidateQueries({ queryKey: ['course', 'detail', course.id] });
      onChanged();
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not delete lesson.', {
        variant: 'error',
      });
    },
  });

  const handleDragStart = (id: string) => (event: DragEvent<HTMLLIElement>): void => {
    setDragId(id);
    event.dataTransfer.effectAllowed = 'move';
    // Required for Firefox to actually start the drag.
    event.dataTransfer.setData('text/plain', id);
  };

  const handleDragOver = (id: string) => (event: DragEvent<HTMLLIElement>): void => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (overId !== id) setOverId(id);
  };

  const handleDragEnd = (): void => {
    setDragId(null);
    setOverId(null);
  };

  const handleDrop = (targetId: string) => (event: DragEvent<HTMLLIElement>): void => {
    event.preventDefault();
    setOverId(null);
    setDragId(null);
    if (!dragId || dragId === targetId) return;

    const current = draftOrder ?? course.lessons;
    const next = current.slice();
    const fromIdx = next.findIndex((l) => l.id === dragId);
    const toIdx = next.findIndex((l) => l.id === targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = next.splice(fromIdx, 1);
    if (!moved) return;
    next.splice(toIdx, 0, moved);

    setDraftOrder(next);
    reorderMutation.mutate(next.map((l) => l.id));
  };

  const handleDelete = async (lesson: LessonSummary): Promise<void> => {
    const ok = await confirm({
      title: 'Delete this lesson?',
      description: `"${lesson.title}" will be removed. Existing progress records that reference it stay intact.`,
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    deleteMutation.mutate(lesson.id);
  };

  return (
    <>
      <RelationPanel
        title="Lessons"
        count={lessons.length}
        description="Ordered curriculum tracks. Drag to reorder."
        action={
          canManage ? (
            <Button
              size="sm"
              icon={<Plus size={14} aria-hidden="true" />}
              onClick={() => setCreateOpen(true)}
            >
              Add lesson
            </Button>
          ) : null
        }
        isEmpty={lessons.length === 0}
        empty={{
          copy: 'No lessons yet.',
          cta: canManage ? (
            <Button
              size="sm"
              icon={<Plus size={14} aria-hidden="true" />}
              onClick={() => setCreateOpen(true)}
            >
              Add lesson
            </Button>
          ) : undefined,
        }}
      >
        <ol className="space-y-2">
          {lessons.map((lesson, idx) => {
            const isEditingRow = editingId === lesson.id;
            const dragEnabled = canManage && !isEditingRow;
            return (
            <li
              key={lesson.id}
              draggable={dragEnabled}
              onDragStart={dragEnabled ? handleDragStart(lesson.id) : undefined}
              onDragOver={dragEnabled ? handleDragOver(lesson.id) : undefined}
              onDragEnd={dragEnabled ? handleDragEnd : undefined}
              onDrop={dragEnabled ? handleDrop(lesson.id) : undefined}
              className={clsx(
                'rounded-md border bg-[color:var(--color-bg)] transition-colors',
                dragId === lesson.id
                  ? 'border-gold-500 opacity-60'
                  : overId === lesson.id
                    ? 'border-gold-500'
                    : 'border-[color:var(--color-border)]',
              )}
            >
              {editingId === lesson.id ? (
                <LessonInlineForm
                  courseId={course.id}
                  lesson={lesson}
                  onCancel={() => setEditingId(null)}
                  onSaved={() => {
                    setEditingId(null);
                    onChanged();
                  }}
                />
              ) : (
                <div className="flex items-center gap-3 px-3 py-2">
                  {canManage ? (
                    <span
                      aria-hidden="true"
                      className="cursor-grab text-[color:var(--color-text-muted)] active:cursor-grabbing"
                      title="Drag to reorder"
                    >
                      <GripVertical size={14} />
                    </span>
                  ) : null}
                  <span className="font-mono text-xs text-[color:var(--color-text-muted)]">
                    {(idx + 1).toString().padStart(2, '0')}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-[color:var(--color-text)]">
                      {lesson.title}
                    </p>
                    {lesson.description ? (
                      <p className="truncate text-xs text-[color:var(--color-text-muted)]">
                        {lesson.description}
                      </p>
                    ) : null}
                  </div>
                  {lesson.expectedDurationMinutes ? (
                    <span className="shrink-0 text-xs text-[color:var(--color-text-muted)]">
                      {lesson.expectedDurationMinutes} min
                    </span>
                  ) : null}
                  {canManage ? (
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setEditingId(lesson.id)}
                        aria-label={`Edit lesson ${lesson.title}`}
                        className="rounded-md p-1.5 text-[color:var(--color-text-muted)] hover:bg-[color:var(--color-surface)] hover:text-[color:var(--color-text)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500"
                      >
                        <Pencil size={14} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          void handleDelete(lesson);
                        }}
                        aria-label={`Delete lesson ${lesson.title}`}
                        className="rounded-md p-1.5 text-[color:var(--color-text-muted)] hover:bg-[color:var(--color-danger)]/10 hover:text-[color:var(--color-danger)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500"
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    </div>
                  ) : null}
                </div>
              )}
            </li>
            );
          })}
        </ol>
      </RelationPanel>

      <LessonCreateDrawer
        courseId={course.id}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          onChanged();
        }}
      />
    </>
  );
}

// Inline editor swaps into the lesson row.
function LessonInlineForm({
  courseId,
  lesson,
  onCancel,
  onSaved,
}: {
  courseId: string;
  lesson: LessonSummary;
  onCancel: () => void;
  onSaved: () => void;
}): JSX.Element {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const initial: LessonFormValues = useMemo(
    () => ({
      title: lesson.title,
      ...(lesson.description ? { description: lesson.description } : {}),
      ...(lesson.expectedDurationMinutes
        ? { expectedDurationMinutes: lesson.expectedDurationMinutes }
        : {}),
    }),
    [lesson],
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LessonFormValues>({
    resolver: zodResolver(lessonFormSchema),
    defaultValues: initial,
  });

  const updateMutation = useMutation({
    mutationFn: (body: LessonFormValues) =>
      apiRequest<{ ok: true }>(`/api/lessons/${encodeURIComponent(lesson.id)}`, {
        method: 'PATCH',
        body,
      }),
  });

  const submit = handleSubmit(async (values) => {
    try {
      await updateMutation.mutateAsync(values);
      toast('Lesson updated.', { variant: 'success' });
      void queryClient.invalidateQueries({ queryKey: ['course', 'detail', courseId] });
      onSaved();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not update lesson.', {
        variant: 'error',
      });
    }
  });

  return (
    <form onSubmit={submit} noValidate className="space-y-3 px-3 py-3">
      <TextField label="Title" error={errors.title?.message} {...register('title')} />
      <TextAreaField
        label="Description"
        optional
        rows={2}
        error={errors.description?.message}
        {...register('description', {
          setValueAs: (v: unknown) => {
            const s = typeof v === 'string' ? v.trim() : '';
            return s === '' ? undefined : s;
          },
        })}
      />
      <TextField
        label="Expected duration (minutes)"
        optional
        type="number"
        min={1}
        max={600}
        error={errors.expectedDurationMinutes?.message}
        {...register('expectedDurationMinutes', {
          setValueAs: (v: unknown) => {
            if (v === '' || v === null || v === undefined) return undefined;
            const n = typeof v === 'number' ? v : Number(v);
            return Number.isFinite(n) ? n : undefined;
          },
        })}
      />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" loading={isSubmitting}>
          Save lesson
        </Button>
      </div>
    </form>
  );
}

function LessonCreateDrawer({
  courseId,
  open,
  onClose,
  onCreated,
}: {
  courseId: string;
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<LessonFormValues>({
    resolver: zodResolver(lessonFormSchema),
    defaultValues: { title: '' },
  });

  // Reset whenever the drawer is reopened so a stale prior submission does
  // not leak into the next session.
  useEffect(() => {
    if (open) reset({ title: '' });
  }, [open, reset]);

  const createMutation = useMutation({
    mutationFn: (body: LessonFormValues) =>
      apiRequest<{ id: string }>(
        `/api/courses/${encodeURIComponent(courseId)}/lessons`,
        { method: 'POST', body },
      ),
  });

  const submit = handleSubmit(async (values) => {
    try {
      await createMutation.mutateAsync(values);
      toast('Lesson added.', { variant: 'success' });
      void queryClient.invalidateQueries({ queryKey: ['course', 'detail', courseId] });
      onCreated();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not add lesson.', {
        variant: 'error',
      });
    }
  });

  return (
    <Drawer open={open} onClose={onClose} title="Add lesson" size="md">
      <form onSubmit={submit} noValidate className="space-y-4">
        <TextField label="Title" error={errors.title?.message} {...register('title')} />
        <TextAreaField
          label="Description"
          optional
          rows={3}
          error={errors.description?.message}
          {...register('description', {
            setValueAs: (v: unknown) => {
              const s = typeof v === 'string' ? v.trim() : '';
              return s === '' ? undefined : s;
            },
          })}
        />
        <TextField
          label="Expected duration (minutes)"
          optional
          type="number"
          min={1}
          max={600}
          error={errors.expectedDurationMinutes?.message}
          {...register('expectedDurationMinutes', {
            setValueAs: (v: unknown) => {
              if (v === '' || v === null || v === undefined) return undefined;
              const n = typeof v === 'number' ? v : Number(v);
              return Number.isFinite(n) ? n : undefined;
            },
          })}
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isSubmitting}>
            Add lesson
          </Button>
        </div>
      </form>
    </Drawer>
  );
}

// ---------------------------------------------------------------------------
// Batches tab
// ---------------------------------------------------------------------------

function BatchesTab({ courseId }: { courseId: string }): JSX.Element {
  const batchesQuery = useQuery({
    queryKey: ['course', 'detail', courseId, 'batches'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: BatchWithEnrolled[] }>('/api/batches', {
        signal,
        query: { courseId, page: 1, pageSize: 100 },
      }),
  });

  const teachersQuery = useQuery({
    // Its own key: other pages cache only the first 100 under ['teachers', 'lookup'].
    queryKey: ['teachers', 'lookup', 'all'] as const,
    queryFn: ({ signal }) =>
      apiListAll<{ id: string; name: string }>('/api/teachers', { signal }).then((items) => ({ items })),
    staleTime: 60_000,
  });

  const teacherName = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of teachersQuery.data?.items ?? []) map.set(t.id, t.name);
    return map;
  }, [teachersQuery.data]);

  const items = batchesQuery.data?.items ?? [];
  const errorState = batchesQuery.isError
    ? {
        message:
          batchesQuery.error instanceof ApiError
            ? batchesQuery.error.message
            : 'Could not load batches.',
        onRetry: (): void => {
          void batchesQuery.refetch();
        },
      }
    : null;

  return (
    <RelationPanel
      title="Batches"
      count={batchesQuery.data ? items.length : undefined}
      description="Batches currently teaching this course."
      loading={batchesQuery.isLoading && !batchesQuery.data}
      error={errorState}
      isEmpty={!batchesQuery.isLoading && items.length === 0}
      empty={{ copy: 'No batches reference this course yet.' }}
    >
      <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)]">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--color-surface)] text-left text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Teacher</th>
              <th className="px-4 py-2">Mode</th>
              <th className="px-4 py-2 text-right">Enrolled</th>
              <th className="px-4 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((batch) => (
              <tr
                key={batch.id}
                className="border-t border-[color:var(--color-border)] transition-colors hover:bg-[color:var(--color-surface)]"
              >
                <td className="px-4 py-2 align-middle font-medium text-[color:var(--color-text)]">
                  {batch.name}
                </td>
                <td className="px-4 py-2 align-middle text-[color:var(--color-text-muted)]">
                  {teacherName.get(batch.teacherId) ?? batch.teacherId.slice(0, 8)}
                </td>
                <td className="px-4 py-2 align-middle text-xs capitalize text-[color:var(--color-text-muted)]">
                  {batch.mode}
                </td>
                <td className="px-4 py-2 text-right align-middle tabular-nums text-[color:var(--color-text-muted)]">
                  {typeof batch.enrolledCount === 'number'
                    ? `${batch.enrolledCount} / ${batch.capacity}`
                    : `— / ${batch.capacity}`}
                </td>
                <td className="px-4 py-2 align-middle">
                  <BatchStatusChip status={batch.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </RelationPanel>
  );
}

function BatchStatusChip({ status }: { status: BatchRecord['status'] }): JSX.Element {
  const tone =
    status === 'active'
      ? 'bg-[color:var(--color-success)]/15 text-[color:var(--color-success)]'
      : status === 'completed'
        ? 'bg-[color:var(--color-surface)] text-[color:var(--color-text)]'
        : status === 'archived'
          ? 'bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)]'
          : 'bg-gold-50 text-gold-700 dark:bg-gold-700/20';
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize',
        tone,
      )}
    >
      {status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Resources tab
// ---------------------------------------------------------------------------

function ResourcesTab({ courseId }: { courseId: string }): JSX.Element {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [downloadingIds, setDownloadingIds] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );

  const resourcesQuery = useQuery({
    queryKey: ['course', 'detail', courseId, 'resources'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: ResourceRecord[] }>('/api/resources', {
        signal,
        query: { courseId },
      }),
  });

  const handleDownload = async (id: string): Promise<void> => {
    if (downloadingIds.has(id)) return;
    setDownloadingIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    try {
      const res = await apiRequest<{ url: string; expiresInSeconds: number }>(
        `/api/resources/${encodeURIComponent(id)}/download-url`,
      );
      window.open(`${API_ORIGIN}${res.url}`, '_blank', 'noopener');
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not start download.', {
        variant: 'error',
      });
    } finally {
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const goToResources = (): void => {
    void navigate({ to: '/academics/resources' });
  };

  const items = resourcesQuery.data?.items ?? [];
  const errorState = resourcesQuery.isError
    ? {
        message:
          resourcesQuery.error instanceof ApiError
            ? resourcesQuery.error.message
            : 'Could not load resources.',
        onRetry: (): void => {
          void resourcesQuery.refetch();
        },
      }
    : null;

  return (
    <RelationPanel
      title="Resources"
      count={resourcesQuery.data ? items.length : undefined}
      description="Files scoped to this course."
      action={
        <Button
          size="sm"
          variant="secondary"
          icon={<Upload size={14} aria-hidden="true" />}
          onClick={goToResources}
        >
          Upload resource
        </Button>
      }
      loading={resourcesQuery.isLoading && !resourcesQuery.data}
      error={errorState}
      isEmpty={!resourcesQuery.isLoading && items.length === 0}
      empty={{
        copy: 'No resources attached to this course yet.',
        cta: (
          <Button
            size="sm"
            icon={<Upload size={14} aria-hidden="true" />}
            onClick={goToResources}
          >
            Upload resource
          </Button>
        ),
      }}
    >
      <ul className="divide-y divide-[color:var(--color-border)] rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)]">
        {items.map((resource) => {
          const isDownloading = downloadingIds.has(resource.id);
          return (
            <li
              key={resource.id}
              className="flex items-center gap-3 px-4 py-2.5"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-[color:var(--color-text)]">
                  {resource.title}
                </p>
                <p className="truncate text-xs text-[color:var(--color-text-muted)]">
                  <span className="font-mono">{resource.mimeType}</span>
                  <span className="mx-1.5">·</span>
                  {humanBytes(resource.sizeBytes)}
                  <span className="mx-1.5">·</span>
                  Uploaded {formatDate(resource.createdAt)}
                </p>
              </div>
              <Button
                size="sm"
                variant="secondary"
                icon={<Download size={14} aria-hidden="true" />}
                onClick={() => {
                  void handleDownload(resource.id);
                }}
                loading={isDownloading}
              >
                Download
              </Button>
            </li>
          );
        })}
        {items.length === 0 && resourcesQuery.isLoading ? (
          <li className="p-4">
            <EmptyState title="Loading…" />
          </li>
        ) : null}
      </ul>
    </RelationPanel>
  );
}
